import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Link } from "react-router-dom";
import { Settings, ArrowRight, Copy, ChevronsUpDown, Check, Loader2, LogIn } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth, SignInButton } from '@clerk/clerk-react';
import AuthButton from './AuthButton';
import { useStudioIQAPI } from '../services/studioiqAPI';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const ManualQuoteInterface = () => {
  const [customerName, setCustomerName] = useState('');
  const [jobId, setJobId] = useState('');
  const [formattedQuote, setFormattedQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);
  
  // Auto-generate job ID on mount
  useEffect(() => {
    const generateJobId = () => {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      return `JOB-${timestamp}-${random}`;
    };
    setJobId(generateJobId());
  }, []);
  
  // Schema data
  const [schema, setSchema] = useState(null);
  
  // Form selections
  const [selections, setSelections] = useState({
    jewelryType: null,
    repairCategory: null,
    subtype: null,
    metalType: null,
    sizeInfo: null,
    stoneCount: null
  });
  
  // Available options for each field (loaded dynamically)
  const [availableOptions, setAvailableOptions] = useState({
    repairCategories: [],
    subtypes: [],
    metalTypes: [],
    sizeInfo: [],
    stoneCount: []
  });

  // Track maximum option counts to determine if field is searchable (even after filtering)
  const [maxOptionCounts, setMaxOptionCounts] = useState({
    repairCategories: 0,
    subtypes: 0,
    metalTypes: 0,
    sizeInfo: 0,
    stoneCount: 0
  });
  
  // Loading states for each field
  const [loadingOptions, setLoadingOptions] = useState({
    repairCategories: false,
    subtypes: false,
    metalTypes: false,
    sizeInfo: false,
    stoneCount: false
  });
  
  // Popover open states
  const [openPopovers, setOpenPopovers] = useState({
    jewelryType: false,
    repairCategory: false,
    subtype: false,
    metalType: false,
    sizeInfo: false,
    stoneCount: false
  });
  
  // Search terms for each field
  const [searchTerms, setSearchTerms] = useState({
    jewelryType: '',
    repairCategory: '',
    subtype: '',
    metalType: '',
    sizeInfo: '',
    stoneCount: ''
  });

  // Debounced search terms for backend search
  const [debouncedSearchTerms, setDebouncedSearchTerms] = useState({
    repairCategory: '',
    subtype: '',
    metalType: '',
    sizeInfo: '',
    stoneCount: ''
  });

  // Debounce search terms (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerms(searchTerms);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerms]);
  
  const { toast } = useToast();
  const { isSignedIn, isLoaded } = useAuth();
  const api = useStudioIQAPI();
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const pollingRef = useRef(null);
  const isSignedInRef = useRef(isSignedIn);
  
  // Keep ref updated with latest isSignedIn value
  useEffect(() => {
    isSignedInRef.current = isSignedIn;
  }, [isSignedIn]);
  
  // Immediately stop polling and complete auth check if isSignedIn becomes true
  useEffect(() => {
    if (isLoaded && isSignedIn && !authCheckComplete) {
      // console.log('[ManualQuote] ✅ isSignedIn changed to true - stopping polling immediately');
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      setAuthCheckComplete(true);
    }
  }, [isLoaded, isSignedIn, authCheckComplete]);
  
  // Wait for Clerk to sync auth state after cross-domain redirect
  // Use ref to always check latest isSignedIn value, not stale closure value
  useEffect(() => {
    if (!isLoaded) {
      setAuthCheckComplete(false);
      return;
    }

    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    // Check for redirect params or cross-domain referrer
    const hasRedirectParams = 
      window.location.search.includes('__clerk') || 
      window.location.hash.includes('__clerk');
    
    // Also check if referrer is from different domain
    let hasCrossDomainReferrer = false;
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        hasCrossDomainReferrer = referrerUrl.hostname !== window.location.hostname;
      } catch (e) {
        // Invalid referrer URL, ignore
      }
    }
    
    const isFromRedirect = hasRedirectParams || hasCrossDomainReferrer;
    
    if (isFromRedirect) {
      // Coming from redirect - poll for auth state changes
      // Use longer polling time since Clerk needs to sync cross-domain
      let checkCount = 0;
      const maxChecks = 60; // 6 seconds total (60 * 100ms)
      
      const pollAuthStatus = () => {
        checkCount++;
        
        // Always check the latest value from ref, not stale closure
        const currentIsSignedIn = isSignedInRef.current;
        
        // console.log(`[ManualQuote] Poll ${checkCount}/${maxChecks}: isSignedIn=${currentIsSignedIn}, isLoaded=${isLoaded}`);
        
        // If user is signed in, we're done
        if (currentIsSignedIn) {
          // console.log('[ManualQuote] ✅ Authentication confirmed after', checkCount, 'checks');
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // If we've checked enough times, give up
        if (checkCount >= maxChecks) {
          console.warn('[ManualQuote] ⏰ Auth check timeout after', maxChecks, 'checks');
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // Check again in 100ms
        pollingRef.current = setTimeout(pollAuthStatus, 100);
      };
      
      // Start polling after initial delay to allow Clerk to sync
      pollingRef.current = setTimeout(pollAuthStatus, 500);
    } else {
      // Normal page load - quick check
      pollingRef.current = setTimeout(() => {
        setAuthCheckComplete(true);
        pollingRef.current = null;
      }, 150);
    }

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isLoaded]); // Only depend on isLoaded, not isSignedIn (we use ref for that)
  
  // Ref for quote section to enable auto-scroll
  const quoteRef = useRef(null);

  // Load schema on mount
  useEffect(() => {
    const loadSchema = async () => {
      setIsLoadingSchema(true);
      try {
        const result = await api.getRepairsSchema();
        if (result.success) {
          setSchema(result);
        } else {
          toast({
            title: "Failed to load schema",
            description: result.error || "Could not load repair options",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Schema load failed:', error);
        toast({
          title: "Failed to load schema",
          description: error.message || "Could not load repair options",
          variant: "destructive"
        });
      } finally {
        setIsLoadingSchema(false);
      }
    };
    
    loadSchema();
  }, [api, toast]);

  // Filter repair categories by jewelry type
  useEffect(() => {
    if (!selections.jewelryType) {
      setAvailableOptions(prev => ({ ...prev, repairCategories: [] }));
      return;
    }

    // Query database for categories available for this jewelry type
    const loadCategories = async () => {
      setLoadingOptions(prev => ({ ...prev, repairCategories: true }));
      try {
        const result = await api.getFieldOptions({
          jewelryType: selections.jewelryType,
          field: 'repair_category',
          search: debouncedSearchTerms.repairCategory || undefined
        });
        
        if (result.success) {
          setAvailableOptions(prev => ({ ...prev, repairCategories: result.options }));
          // Track max count for searchability
          setMaxOptionCounts(prev => ({
            ...prev,
            repairCategories: Math.max(prev.repairCategories, result.options?.length || 0)
          }));
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoadingOptions(prev => ({ ...prev, repairCategories: false }));
      }
    };
    
    loadCategories();
  }, [selections.jewelryType, debouncedSearchTerms.repairCategory, api]);

  // Load subtype options when category is selected (with search)
  useEffect(() => {
    if (!selections.jewelryType || !selections.repairCategory) {
      setAvailableOptions(prev => ({ ...prev, subtypes: [] }));
      return;
    }

    const loadSubtypes = async () => {
      setLoadingOptions(prev => ({ ...prev, subtypes: true }));
      try {
        const result = await api.getFieldOptions({
          category: selections.repairCategory,
          jewelryType: selections.jewelryType,
          field: 'subtype',
          search: debouncedSearchTerms.subtype || undefined
        });
        
        if (result.success) {
          setAvailableOptions(prev => ({ ...prev, subtypes: result.options }));
          // Track max count for searchability
          setMaxOptionCounts(prev => ({
            ...prev,
            subtypes: Math.max(prev.subtypes, result.options?.length || 0)
          }));
        }
      } catch (error) {
        console.error('Failed to load subtypes:', error);
      } finally {
        setLoadingOptions(prev => ({ ...prev, subtypes: false }));
      }
    };
    
    loadSubtypes();
  }, [selections.jewelryType, selections.repairCategory, debouncedSearchTerms.subtype, api]);

  // Load metal type options when category is selected (with search)
  // If subtype exists, wait for it to be selected; otherwise load immediately
  useEffect(() => {
    if (!selections.jewelryType || !selections.repairCategory) {
      setAvailableOptions(prev => ({ ...prev, metalTypes: [] }));
      return;
    }

    // If subtypes exist for this category, wait for subtype to be selected
    if (availableOptions.subtypes.length > 0 && !selections.subtype) {
      setAvailableOptions(prev => ({ ...prev, metalTypes: [] }));
      return;
    }

    const loadMetalTypes = async () => {
      setLoadingOptions(prev => ({ ...prev, metalTypes: true }));
      try {
        const result = await api.getFieldOptions({
          category: selections.repairCategory,
          jewelryType: selections.jewelryType,
          field: 'metal_type_color',
          ...(selections.subtype && { subtype: selections.subtype }),
          search: debouncedSearchTerms.metalType || undefined
        });
        
        if (result.success) {
          setAvailableOptions(prev => ({ ...prev, metalTypes: result.options }));
          // Track max count for searchability
          setMaxOptionCounts(prev => ({
            ...prev,
            metalTypes: Math.max(prev.metalTypes, result.options?.length || 0)
          }));
        }
      } catch (error) {
        console.error('Failed to load metal types:', error);
      } finally {
        setLoadingOptions(prev => ({ ...prev, metalTypes: false }));
      }
    };
    
    loadMetalTypes();
  }, [selections.jewelryType, selections.repairCategory, selections.subtype, availableOptions.subtypes, debouncedSearchTerms.metalType, api]);

  // Load size info options when required fields are selected (with search)
  // Size info can load even if metal type doesn't exist (when metal type has no options)
  useEffect(() => {
    if (!selections.jewelryType || !selections.repairCategory) {
      setAvailableOptions(prev => ({ ...prev, sizeInfo: [] }));
      return;
    }

    // If metal type has options, wait for it to be selected
    if (availableOptions.metalTypes.length > 0 && !selections.metalType) {
      setAvailableOptions(prev => ({ ...prev, sizeInfo: [] }));
      return;
    }

    // If subtypes exist for this category, wait for subtype to be selected
    if (availableOptions.subtypes.length > 0 && !selections.subtype) {
      setAvailableOptions(prev => ({ ...prev, sizeInfo: [] }));
      return;
    }

    const loadSizeInfo = async () => {
      setLoadingOptions(prev => ({ ...prev, sizeInfo: true }));
      try {
            const result = await api.getFieldOptions({
              category: selections.repairCategory,
              jewelryType: selections.jewelryType,
              field: 'size_info',
              ...(selections.subtype && { subtype: selections.subtype }),
              ...(selections.metalType && { metalType: selections.metalType }),
              search: debouncedSearchTerms.sizeInfo || undefined
            });
        
        if (result.success) {
          setAvailableOptions(prev => ({ ...prev, sizeInfo: result.options }));
          // Track max count for searchability
          setMaxOptionCounts(prev => ({
            ...prev,
            sizeInfo: Math.max(prev.sizeInfo, result.options?.length || 0)
          }));
        }
      } catch (error) {
        console.error('Failed to load size info:', error);
      } finally {
        setLoadingOptions(prev => ({ ...prev, sizeInfo: false }));
      }
    };
    
    loadSizeInfo();
  }, [selections.jewelryType, selections.repairCategory, selections.subtype, selections.metalType, availableOptions.subtypes, availableOptions.metalTypes, debouncedSearchTerms.sizeInfo, api]);

  // Load stone count options when required fields are selected
  useEffect(() => {
    // Stone count requires: jewelryType, repairCategory
    // Metal type is only required if it has options
    // If subtypes exist, subtype must be selected; if sizeInfo has options, it must be selected
    if (!selections.jewelryType || !selections.repairCategory) {
      setAvailableOptions(prev => ({ ...prev, stoneCount: [] }));
      return;
    }

    // If metal type has options, wait for it to be selected
    if (availableOptions.metalTypes.length > 0 && !selections.metalType) {
      setAvailableOptions(prev => ({ ...prev, stoneCount: [] }));
      return;
    }

    // If subtypes exist for this category, wait for subtype to be selected
    if (availableOptions.subtypes.length > 0 && !selections.subtype) {
      setAvailableOptions(prev => ({ ...prev, stoneCount: [] }));
      return;
    }

    // If sizeInfo has options available, we need it selected first
    if (availableOptions.sizeInfo.length > 0 && !selections.sizeInfo) {
      setAvailableOptions(prev => ({ ...prev, stoneCount: [] }));
      return;
    }

    const loadStoneCount = async () => {
      setLoadingOptions(prev => ({ ...prev, stoneCount: true }));
      try {
            const result = await api.getFieldOptions({
              category: selections.repairCategory,
              jewelryType: selections.jewelryType,
              field: 'stone_count',
              ...(selections.subtype && { subtype: selections.subtype }),
              ...(selections.metalType && { metalType: selections.metalType }),
              ...(selections.sizeInfo && { sizeInfo: selections.sizeInfo }),
              search: debouncedSearchTerms.stoneCount || undefined
            });
        
        if (result.success) {
          console.log('[ManualMode] Stone count options loaded:', {
            count: result.options?.length || 0,
            options: result.options
          });
          setAvailableOptions(prev => ({ ...prev, stoneCount: result.options }));
          // Track max count for searchability
          setMaxOptionCounts(prev => ({
            ...prev,
            stoneCount: Math.max(prev.stoneCount, result.options?.length || 0)
          }));
        } else {
          console.warn('[ManualMode] Stone count options not loaded:', result);
        }
      } catch (error) {
        console.error('[ManualMode] Failed to load stone count:', error);
        toast({
          title: "Failed to load stone count options",
          description: error.message || "Please try again",
          variant: "destructive"
        });
      } finally {
        setLoadingOptions(prev => ({ ...prev, stoneCount: false }));
      }
    };
    
    loadStoneCount();
  }, [selections.jewelryType, selections.repairCategory, selections.subtype, selections.metalType, selections.sizeInfo, availableOptions.subtypes, availableOptions.sizeInfo, availableOptions.metalTypes, debouncedSearchTerms.stoneCount, api, toast]);

  // Handle selection change - clears subsequent fields
  const handleSelectionChange = useCallback((field, value) => {
    setSelections(prev => {
      const newSelections = { ...prev, [field]: value };
      
      // Clear all fields after the changed one
      const fieldOrder = ['jewelryType', 'repairCategory', 'subtype', 'metalType', 'sizeInfo', 'stoneCount'];
      const changedIndex = fieldOrder.indexOf(field);
      
      fieldOrder.slice(changedIndex + 1).forEach(f => {
        newSelections[f] = null;
      });
      
      return newSelections;
    });
    
    // Close popover
    setOpenPopovers(prev => ({ ...prev, [field]: false }));
  }, []);

  // Determine which fields are enabled
  const isCategoryEnabled = selections.jewelryType !== null;
  const isSubtypeEnabled = selections.jewelryType !== null && selections.repairCategory !== null;
  // Metal type is enabled if:
  // - Repair category is selected AND
  // - (Subtype is selected OR no subtypes exist for this category)
  // Metal type field is only shown if options exist (handled in render)
  const isMetalTypeEnabled = isSubtypeEnabled && (selections.subtype !== null || availableOptions.subtypes.length === 0);
  // Size info is enabled if:
  // - Base fields selected (jewelry type, category)
  // - AND (metal type selected OR no metal type options exist)
  // - AND (subtype selected OR no subtype options exist)
  // - AND sizeInfo has options available
  const isSizeInfoEnabled = selections.jewelryType !== null && 
    selections.repairCategory !== null && 
    (availableOptions.metalTypes.length === 0 || selections.metalType !== null) &&
    (availableOptions.subtypes.length === 0 || selections.subtype !== null) &&
    availableOptions.sizeInfo.length > 0;
  // Stone count is enabled if:
  // - Base fields selected (jewelry type, category)
  // - AND (metal type selected OR no metal type options exist)
  // - AND (subtype selected OR no subtype options exist)
  // - AND (sizeInfo selected OR no sizeInfo options exist)
  // - AND stoneCount has options available
  const isStoneCountEnabled = selections.jewelryType !== null && 
    selections.repairCategory !== null && 
    (availableOptions.metalTypes.length === 0 || selections.metalType !== null) &&
    (availableOptions.subtypes.length === 0 || selections.subtype !== null) &&
    (availableOptions.sizeInfo.length === 0 || selections.sizeInfo !== null) &&
    availableOptions.stoneCount.length > 0;

  // Generate quote
  const handleGenerateQuote = async () => {
    if (!isSignedIn) {
      toast({
        title: "Please sign in",
        description: "Authentication required",
        variant: "destructive"
      });
      return;
    }

    // Validate required fields
    if (!selections.jewelryType || !selections.repairCategory) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Metal type is only required if options exist
    if (availableOptions.metalTypes.length > 0 && !selections.metalType) {
      toast({
        title: "Missing metal type",
        description: "Please select metal type",
        variant: "destructive"
      });
      return;
    }

    // Check conditional fields - only require if options exist
    if (availableOptions.sizeInfo.length > 0 && !selections.sizeInfo) {
      toast({
        title: "Missing size information",
        description: "Please select size information",
        variant: "destructive"
      });
      return;
    }

    if (availableOptions.stoneCount.length > 0 && selections.stoneCount === null) {
      toast({
        title: "Missing stone count",
        description: "Please select stone count",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      // First, we need to find the exact repair in the database to get repairId, tiers, etc.
      // The logic worker will handle this, but we need to ensure all required fields are present
      // For now, let's build the structured job and let the logic worker look up the repair
      
      // Build structured job in the format expected by logic worker
      const structuredJob = {
        "Customer Name": customerName || 'Customer',
        "Items": [{
          "Jewelry Type": selections.jewelryType,
          "Repairs": [{
            "Repair Type": selections.repairCategory,
            // Only include metal type if it was selected (options exist and user selected one)
            ...(selections.metalType && { "Metal Type & Color": selections.metalType }),
            ...(selections.subtype && { subtype: selections.subtype }),
            ...(selections.sizeInfo && { size_info: selections.sizeInfo }),
            ...(selections.stoneCount !== null && { stoneCount: selections.stoneCount })
          }]
        }]
      };

      // Call quote job - the logic worker will look up the exact repair from the database
      const result = await api.quoteJob(structuredJob);
      setFormattedQuote(result.formattedQuote);
      
      // Scroll to quote section after a brief delay to ensure DOM is updated
      setTimeout(() => {
        if (quoteRef.current) {
          quoteRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
      
      toast({
        title: "Quote generated",
        description: "Your quote is ready"
      });
    } catch (error) {
      console.error('Quote generation failed:', error);
      toast({
        title: "Failed to generate quote",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyQuote = () => {
    if (!formattedQuote) return;
    navigator.clipboard.writeText(formattedQuote);
    toast({
      title: "Quote copied",
      description: "Ready to share"
    });
  };

  // Reset all form fields and quote
  const handleReset = () => {
    setSelections({
      jewelryType: null,
      repairCategory: null,
      subtype: null,
      metalType: null,
      sizeInfo: null,
      stoneCount: null
    });
    setAvailableOptions({
      repairCategories: [],
      subtypes: [],
      metalTypes: [],
      sizeInfo: [],
      stoneCount: []
    });
    setFormattedQuote(null);
    setSearchTerms({
      jewelryType: '',
      repairCategory: '',
      subtype: '',
      metalType: '',
      sizeInfo: '',
      stoneCount: ''
    });
    setOpenPopovers({
      jewelryType: false,
      repairCategory: false,
      subtype: false,
      metalType: false,
      sizeInfo: false,
      stoneCount: false
    });
    // Generate new job ID
    const generateJobId = () => {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      return `JOB-${timestamp}-${random}`;
    };
    setJobId(generateJobId());
  };

  // Render searchable dropdown
  const renderSearchableDropdown = (field, label, options, enabled, placeholder, isLoading) => {
    // Field is searchable if:
    // 1. It currently has more than 10 options, OR
    // 2. It has ever had more than 10 options (based on max count we've seen), OR
    // 3. User has typed something (so they can continue searching even if no results)
    const currentCount = options.length;
    const maxCount = maxOptionCounts[field] || 0;
    const hasSearchTerm = searchTerms[field] && searchTerms[field].trim().length > 0;
    const isSearchable = currentCount > 10 || maxCount > 10 || hasSearchTerm;
    const displayValue = selections[field] || placeholder;
    
    // Options are already filtered by backend search, no need for client-side filtering
    const filteredOptions = options;
    
    return (
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <span>{label}</span>
          {isSearchable && options.length > 0 && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              🔍 {options.length} options
            </Badge>
          )}
          {!enabled && (
            <span className="text-xs text-slate-400 font-normal">(Select previous field first)</span>
          )}
        </label>
        
        {/* Use Popover for both searchable and non-searchable for consistent styling */}
        <Popover 
          open={openPopovers[field]} 
          onOpenChange={(open) => {
            setOpenPopovers(prev => ({ ...prev, [field]: open }));
            if (!open) {
              // Clear search when closing
              setSearchTerms(prev => ({ ...prev, [field]: '' }));
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!enabled || isLoading}
              className={`w-full justify-between text-left font-normal h-11 ${
                !enabled 
                  ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed' 
                  : selections[field]
                  ? 'bg-white border-slate-300 text-slate-900 hover:bg-slate-50'
                  : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className={selections[field] ? "text-slate-900 font-medium" : "text-slate-500"}>
                {displayValue}
              </span>
              {isLoading ? (
                <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
              ) : (
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-[var(--radix-popover-trigger-width)] p-0 shadow-xl border-2 border-slate-200" 
            align="start"
            side="bottom"
            sideOffset={4}
            collisionPadding={8}
          >
            <Command shouldFilter={false} className="rounded-lg">
              {/* Only show search input for searchable dropdowns */}
              {isSearchable && (
                <CommandInput 
                  placeholder={`Type to search ${label.toLowerCase()}...`}
                  value={searchTerms[field]}
                  onValueChange={(value) => {
                    setSearchTerms(prev => ({ ...prev, [field]: value }));
                  }}
                  className="h-10 border-b"
                />
              )}
              <CommandList className="max-h-[300px]">
                <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading options...</span>
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div>
                      <p className="font-medium">No options found</p>
                      <p className="text-xs mt-1">Try a different search term</p>
                    </div>
                  ) : (
                    'Type to search...'
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {filteredOptions.map((option, index) => (
                    <CommandItem
                      key={index}
                      value={option}
                      onSelect={() => {
                        handleSelectionChange(field, option);
                      }}
                      className="cursor-pointer hover:bg-slate-100 transition-colors py-2.5 px-3"
                    >
                      <Check
                        className={`mr-3 h-4 w-4 ${
                          selections[field] === option ? 'opacity-100 text-blue-600' : 'opacity-0'
                        }`}
                      />
                      <span className={selections[field] === option ? 'font-medium text-slate-900' : 'text-slate-700'}>
                        {option}
                      </span>
                    </CommandItem>
                  ))}
                  {filteredOptions.length === 0 && searchTerms[field] && (
                    <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t">
                      No results found for "{searchTerms[field]}". Try a different search term.
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  if (isLoadingSchema) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-slate-600" />
          <p className="text-slate-600">Loading repair options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-slate-900">StudioIQ</h1>
            <Badge variant="outline" className="text-xs bg-blue-50">
              Manual Mode
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {isSignedIn && (
              <Button asChild variant="default" size="sm">
                <Link to="/dashboard">
                  <Settings className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
              </Button>
            )}
            <AuthButton />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        
        {/* Show login message if not signed in */}
        {!authCheckComplete ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto"></div>
                <p className="text-slate-600">Checking authentication...</p>
              </div>
            </CardContent>
          </Card>
        ) : !isSignedIn ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <LogIn className="h-12 w-12 text-slate-400 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Sign In Required</h3>
                  <p className="text-slate-600 mt-2">Please sign in to generate quotes</p>
                </div>
                <SignInButton mode="modal">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                    Sign In to Continue
                  </Button>
                </SignInButton>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Customer Info */}
            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Customer Name
                    </label>
                    <Input
                      placeholder="Enter customer name"
                      value={customerName}
                      onChange={(e) => {
                        setCustomerName(e.target.value);
                      }}
                      className="border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Job ID
                    </label>
                    <Input
                      placeholder="Auto-generated or custom"
                      value={jobId}
                      onChange={(e) => setJobId(e.target.value)}
                      className="border-slate-200 focus:border-slate-400 transition-colors"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Repair Selection Form */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    Select Repair Details
                  </CardTitle>
                  {formattedQuote && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      className="text-slate-700 hover:bg-slate-100"
                    >
                      Try Another
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {/* Jewelry Type */}
                {renderSearchableDropdown(
                  'jewelryType',
                  'Jewelry Type',
                  schema?.jewelryTypes || [],
                  true,
                  'Select jewelry type...',
                  false
                )}

                {/* Repair Category */}
                {renderSearchableDropdown(
                  'repairCategory',
                  'Repair Category',
                  availableOptions.repairCategories,
                  isCategoryEnabled,
                  isCategoryEnabled ? 'Select repair category...' : 'Please select jewelry type first',
                  loadingOptions.repairCategories
                )}

                {/* Subtype - Show only if options exist (after loading completes) */}
                {availableOptions.subtypes.length > 0 && (
                  renderSearchableDropdown(
                    'subtype',
                    'Subtype',
                    availableOptions.subtypes,
                    isSubtypeEnabled,
                    isSubtypeEnabled ? 'Select subtype...' : 'Please select repair category first',
                    false
                  )
                )}

                {/* Metal Type - Show only if options exist (after loading completes) */}
                {availableOptions.metalTypes.length > 0 && (
                  renderSearchableDropdown(
                    'metalType',
                    'Metal Type',
                    availableOptions.metalTypes,
                    isMetalTypeEnabled,
                    isMetalTypeEnabled 
                      ? 'Select metal type...' 
                      : availableOptions.subtypes.length > 0 
                        ? 'Please select subtype first' 
                        : 'Please select repair category first',
                    loadingOptions.metalTypes
                  )
                )}

                {/* Size Info - Show only if options exist (after loading completes) */}
                {availableOptions.sizeInfo.length > 0 && (
                  renderSearchableDropdown(
                    'sizeInfo',
                    'Size Info',
                    availableOptions.sizeInfo,
                    isSizeInfoEnabled,
                    isSizeInfoEnabled 
                      ? 'Select size info...' 
                      : availableOptions.metalTypes.length > 0 && !selections.metalType
                        ? 'Please select metal type first'
                        : availableOptions.subtypes.length > 0 && !selections.subtype
                          ? 'Please select subtype first'
                          : 'Please select repair category first',
                    false
                  )
                )}

                {/* Stone Count - Show only if options exist (after loading completes) */}
                {availableOptions.stoneCount.length > 0 && (
                  renderSearchableDropdown(
                    'stoneCount',
                    'Stone Count',
                    availableOptions.stoneCount,
                    isStoneCountEnabled,
                    isStoneCountEnabled 
                      ? 'Select stone count...' 
                      : availableOptions.sizeInfo.length > 0 && !selections.sizeInfo
                        ? 'Please select size info first'
                        : availableOptions.metalTypes.length > 0 && !selections.metalType
                          ? 'Please select metal type first'
                          : availableOptions.subtypes.length > 0 && !selections.subtype
                            ? 'Please select subtype first'
                            : 'Please select repair category first',
                    false
                  )
                )}

                {/* Generate Button */}
                <Button 
                  onClick={handleGenerateQuote}
                  disabled={isLoading || !isMetalTypeEnabled || (availableOptions.sizeInfo.length > 0 && !selections.sizeInfo) || (availableOptions.stoneCount.length > 0 && selections.stoneCount === null) || loadingOptions.subtypes || loadingOptions.metalTypes || loadingOptions.sizeInfo || loadingOptions.stoneCount}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 mt-6"
                >
                  {isLoading || loadingOptions.subtypes || loadingOptions.metalTypes || loadingOptions.sizeInfo || loadingOptions.stoneCount ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{isLoading ? 'Generating Quote...' : 'Loading options...'}</span>
                    </div>
                  ) : (
                    'Generate Quote'
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Quote Output */}
        {formattedQuote && (
          <Card ref={quoteRef} className="border-none shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Quote Generated Successfully
                </CardTitle>
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  Ready
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Success Message */}
              <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-800">Quote Ready</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Your quote has been generated successfully. Review the details below and copy to share with your customer.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardContent className="space-y-6">
              {/* Formatted Quote Display */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <pre className="whitespace-pre-wrap text-sm text-slate-900 font-mono leading-relaxed">
                  {formattedQuote}
                </pre>
              </div>

              {/* Copy Quote Button */}
              <Button 
                onClick={handleCopyQuote}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-6 rounded-lg transition-colors"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Quote
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Switch to AI Mode */}
        <div className="text-center py-4">
          <Button 
            variant="ghost"
            onClick={() => window.location.href = '/'}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Switch to AI Mode
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualQuoteInterface;
